import {
  type OrderStatus as ApiOrderStatus,
  type Order,
  type OrderFailureReason,
  OrderStatusExpired,
  OrderStatusFailed,
  OrderStatusFilled,
  type OrderStatusReason,
  OrderStatusReasonOrderDescIdTooLow,
  OrderStatusReasonUnspecified,
  OrderTypeIncreasePositionCollateral,
} from '@/api/spec.ts';
import type { MarketData } from '@/common/MarketData.ts';
import { notify } from '@/feature/notifications';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { OrderAction, OrderStatus, OrderType } from '@/feature/order_entry/order_types.ts';
import type { ViewOrder } from '@/feature/order_entry/ViewOrder.ts';
import type { OrderPostingStatus } from '../OpenOrdersStore.ts';
import { mapOrderToViewOrder } from '../types/mapOrderToViewOrder.ts';
import { buildOrderBaseMessage } from './helpers.ts';
import { getOrderErrorInfo } from './orderErrorHandling.ts';
import { getOrderStatusFacts } from './orderStatusFacts.ts';

// Which statuses raise a toast lives in `orderStatusFacts` as `notifies`: `OpenOrdersStore` reads
// the same answer to decide whether the posting stays quiet. As separate lists they disagreed about
// `PartiallyFilled` and each suppressed its toast expecting the other to raise one.

export function isChangeOrderUiKey(uiKey?: string): boolean {
  return uiKey?.startsWith('change:') ?? false;
}

/** @param includeFailureDetail spell out why a `failed` order failed, not just the headline. */
export function getOrderUpdateMessage(order: ViewOrder, includeFailureDetail = false): string {
  const baseMessage = buildOrderBaseMessage({
    marketId: order.marketId,
    side: order.side,
    isClose: order.action === OrderAction.close,
    orderType: order.type,
    size: order.originalSize,
    price: order.fillingPrice || order.price, // TODO have a clear rule here
  });

  switch (order.status) {
    case OrderStatus.open:
      return `${baseMessage} placed successfully`;
    case OrderStatus.partial:
      return `${baseMessage} partially filled`;
    case OrderStatus.filled:
      return `${baseMessage} completely filled`;
    case OrderStatus.expired:
      return `${baseMessage} expired`;
    case OrderStatus.triggered:
      return `${baseMessage} triggered`;
    case OrderStatus.failed: {
      const errorInfo = getOrderErrorInfo(order.statusReason ?? OrderStatusReasonUnspecified, undefined, order.failureReason);
      return includeFailureDetail
        ? `${baseMessage} failed: ${errorInfo.title}. ${errorInfo.message}`
        : `${baseMessage} failed: ${errorInfo.title}`;
    }
    default:
      return `Order ${order.oid} status updated`;
  }
}

export type PostingContext = {
  orderData?: OrderFormData;
  errorReason?: OrderStatusReason;
  /** Refines `errorReason` when the exchange published a cause (`Order.fr`). */
  failureReason?: OrderFailureReason;
  /**
   * The copy the failing caller already built. Preferred over recomputing from `errorReason`,
   * which drops the `context` half — every FE-side failure (timeouts, backstop, build errors)
   * reports `Unspecified` and used to render as a bare "an unspecified error occurred".
   */
  errorMessage?: string;
  requestId?: number;
  uiKey?: string;
  retryCount?: number;
  oid?: number;
};

export function getPostingStatusMessage(status: OrderPostingStatus, context: PostingContext = {}): string {
  const { orderData, errorReason, failureReason, errorMessage, retryCount, uiKey } = context;
  const isChange = isChangeOrderUiKey(uiKey);

  switch (status) {
    case 'confirming':
      if (retryCount && retryCount > 0) {
        return isChange ? 'Retrying order update...' : 'Retrying order confirmation...';
      }
      return isChange ? 'Updating order...' : 'Confirming order...';
    case 'confirmed':
      if (orderData) {
        const baseMessage = buildOrderBaseMessage({
          marketId: orderData.marketId,
          side: orderData.side,
          isClose: orderData.reduceOnly,
          orderType: orderData.orderType,
          size: orderData.size,
          // Market and Stop Market forms can still hold the Limit tab's price.
          price:
            orderData.orderType === OrderType.limit || orderData.orderType === OrderType.stopLimit
              ? orderData.price
              : undefined,
        });
        return isChange ? `${baseMessage} updated successfully.` : `${baseMessage} placed successfully.`;
      }
      if (isChange) {
        return 'Order updated successfully.';
      }
      return 'Order cancelled successfully.';
    case 'failed':
      if (errorMessage) {
        return errorMessage;
      }
      if (errorReason !== undefined) {
        const errorInfo = getOrderErrorInfo(errorReason, undefined, failureReason);
        return errorInfo.message;
      }
      return 'Order Failed. Please try again.';
    default:
      return '';
  }
}

export type OrderStatusUpdateContext = {
  previousStatus?: ApiOrderStatus;
  /**
   * The posting slot's uiKey, only while that slot still owns this order. Gates edit-flow
   * suppression — deliberately NOT the key the toast is filed under, because widening it to
   * every update would swallow the later lifecycle events of an edited order.
   */
  postingUiKey?: string;
  /**
   * Identity of the user action this order belongs to, stable for the order's whole life.
   * The exchange mints a fresh `oid` per posting attempt and the FE rotates `rq` per retry, so
   * `order:{oid}`/`rq` both fragment one click into several un-collapsible notifications.
   */
  actionKey?: string;
  markets?: ReadonlyMap<number, MarketData>;
  /** The posting toast will follow and spell out the reason, so this toast can stay short. */
  postingWillExplain?: boolean;
};

/**
 * Creates notification for order status changes.
 * ReplaceKey precedence: `posting:{actionKey}` > `tx:{txid}` (on-chain flow) > `order:{oid}`.
 * `handlePostingStatusChange` follows the same precedence so both toasts for one failure collapse.
 */
export function handleOrderStatusUpdate(order: Order, context: OrderStatusUpdateContext = {}): void {
  const { previousStatus, postingUiKey, actionKey, markets, postingWillExplain = false } = context;

  if (previousStatus === order.st) {
    return;
  }

  // Chart/API order edits use the posting toast (`change:{oid}`); skip close-order phrasing here.
  if (isChangeOrderUiKey(postingUiKey)) {
    return;
  }

  const shouldNotify = getOrderStatusFacts(order.st).notifies;

  if (shouldNotify) {
    // Hide info/success notifications for Add Margin orders, as they are handled by PositionStore
    if (order.t === OrderTypeIncreasePositionCollateral) {
      if (order.st !== OrderStatusFailed && order.st !== OrderStatusExpired) {
        return;
      }
      if (order.st === OrderStatusFailed && order.sr === OrderStatusReasonOrderDescIdTooLow) {
        return;
      }
    }

    const viewOrder = mapOrderToViewOrder(order, markets);

    // Skip filled close-action toasts; the position-status toast covers them with realized PnL
    if (viewOrder.action === OrderAction.close && order.st === OrderStatusFilled) {
      return;
    }

    // With no posting toast to follow, nothing else explains the failure — carry it here.
    const message = getOrderUpdateMessage(viewOrder, order.st === OrderStatusFailed && !postingWillExplain);
    let severity: 'success' | 'info' | 'warning' | 'error' = 'success';
    let debugInfo: string | undefined;

    if (order.st === OrderStatusFailed) {
      if (order.sr != null) {
        const errorInfo = getOrderErrorInfo(order.sr, undefined, order.fr);
        severity = errorInfo.severity;
      } else {
        severity = 'error';
      }
    } else if (order.st === OrderStatusExpired) {
      severity = 'error';
    }

    if (severity === 'error' || severity === 'warning') {
      debugInfo = `Request ID: ${order.rq}`;
    }

    // Update messages are partial: `c` (creation block/tx) is dropped after the first event,
    // so fall back to `at.txid` (this event's tx) to keep on-chain dedup working
    const txid = order.c?.txid || order.at?.txid;
    const replaceKey = actionKey ? `posting:${actionKey}` : txid ? `tx:${txid}` : `order:${order.oid}`;
    notify(message, severity, { debugInfo, replaceKey });
  }
}

export function handlePostingStatusChange(
  newStatus: OrderPostingStatus,
  previousStatus: OrderPostingStatus,
  context: PostingContext
): void {
  if (previousStatus === newStatus) {
    return;
  }

  const { errorReason, failureReason, requestId, uiKey, oid } = context;
  const message = getPostingStatusMessage(newStatus, context);
  if (!message) {
    return;
  }

  // Same precedence as `handleOrderStatusUpdate`, so the WS toast and this one collapse into a
  // single notification per user action. Preferring `order:{oid}` here filed the pair under two
  // different keys and made every failure at least two toasts.
  const replaceKey = uiKey ? `posting:${uiKey}` : oid ? `order:${oid}` : undefined;

  switch (newStatus) {
    case 'confirming':
      // Use waiting:true so it shows immediately but doesn't persist to history
      notify(message, 'info', { manualDismiss: true, waiting: true, replaceKey });
      break;
    case 'confirmed':
      notify(message, 'success', { replaceKey });
      break;
    case 'failed': {
      const debugInfo = requestId != null ? `Request ID: ${requestId}` : undefined;
      const severity = errorReason != null ? getOrderErrorInfo(errorReason, undefined, failureReason).severity : 'error';
      notify(message, severity, { debugInfo, replaceKey });
      break;
    }
    default:
      break;
  }
}
